if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();

      this.customPickupEnabled = this.dataset.customPickupEnabled === 'true';
      this.customPickupPrimaryLocation = this.dataset.customPickupPrimaryLocation || '';
      this.customPickupStoreNames = this.dataset.customPickupStoreNames || '';
      this.customPickupStoreConfigs = this.dataset.customPickupStoreConfigs || '[]';
      this.customPickupTitleTemplate = this.dataset.customPickupTitleTemplate || '';
      this.customPickupAvailableLabel = this.dataset.customPickupAvailableLabel || '';
      this.customPickupViewStoreInfoLabel = this.dataset.customPickupViewStoreInfoLabel || '';
      this.customPickupViewStoresInfoLabel = this.dataset.customPickupViewStoresInfoLabel || '';
      this.customPickupMultipleLocationsJoiner = this.dataset.customPickupMultipleLocationsJoiner || ' and ';
      this.customPickupCheckOtherStoresLabel = this.dataset.customPickupCheckOtherStoresLabel || '';
      this.customPickupHeading = this.dataset.customPickupHeading || '';

      if (!this.hasAttribute('available')) return;

      this.onClickRefreshList = this.onClickRefreshList.bind(this);
      this.onDrawerToggle = this.onDrawerToggle.bind(this);
      this.fetchAvailability(this.dataset.variantId);

    }
    normalizeLocationName(value) {
      return (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }
    escapeHtml(value) {
      return (value || '').replace(/[&<>"']/g, (match) => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        };

        return entities[match] || match;
      });
    }
    formatCustomPickupStoreLabel(label) {
      const trimmedLabel = (label || '').trim();

      if (/^store\s+/i.test(trimmedLabel)) {
        return `${trimmedLabel.replace(/^store\s+/i, '')} store`;
      }

      return trimmedLabel;
    }
    getAvailableIcon() {
      return '<svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0005 1.00061L5.00018 9.00061L1 5.00079" stroke="#21D17D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
    getCustomPickupLocations(sectionInnerHTML) {
      return Array.from(sectionInnerHTML.querySelectorAll('.pickup-availability-list__item')).map((item) => {
        return {
          name: item.querySelector('.pickup-availability-list__location')?.textContent?.trim() || '',
          addressHtml: item.querySelector('.pickup-availability-list__address')?.innerHTML || '',
          phoneHtml: item.querySelector('.pickup-availability-list__phone')?.innerHTML || ''
        };
      }).filter((location) => location.name);
    }
    getConfiguredCustomPickupStoreNames() {
      return (this.customPickupStoreNames || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }
    getConfiguredCustomPickupStoreConfigs() {
      try {
        return JSON.parse(this.customPickupStoreConfigs || '[]').filter((store) => store && (store.match || store.label));
      } catch (error) {
        return [];
      }
    }
    getConfiguredCustomPickupLocations(locations) {
      const configuredStoreConfigs = this.getConfiguredCustomPickupStoreConfigs();
      if (configuredStoreConfigs.length) {
        const configuredLocations = configuredStoreConfigs.map((store) => {
          const normalizedMatch = this.normalizeLocationName(store.match || store.label);
          const matchedLocation = locations.find((location) => this.normalizeLocationName(location.name).includes(normalizedMatch));

          return {
            name: this.formatCustomPickupStoreLabel(store.label || matchedLocation?.name || store.match || ''),
            match: store.match || store.label || '',
            addressHtml: matchedLocation?.addressHtml || store.addressHtml || '',
            phoneHtml: matchedLocation?.phoneHtml || this.escapeHtml(store.phone || '')
          };
        }).filter((location) => location.name);

        return configuredLocations.filter((location, index, collection) => {
          return collection.findIndex((candidate) => this.normalizeLocationName(candidate.name) === this.normalizeLocationName(location.name)) === index;
        });
      }

      const configuredStoreNames = this.getConfiguredCustomPickupStoreNames();

      if (!configuredStoreNames.length) {
        return locations;
      }

      const configuredLocations = configuredStoreNames.map((storeName) => {
        const matchedLocation = locations.find((location) => this.normalizeLocationName(location.name).includes(this.normalizeLocationName(storeName)));

        return matchedLocation || {
          name: storeName,
          addressHtml: '',
          phoneHtml: ''
        };
      });

      return configuredLocations.filter((location, index, collection) => {
        return collection.findIndex((candidate) => this.normalizeLocationName(candidate.name) === this.normalizeLocationName(location.name)) === index;
      });
    }
    getPrimaryCustomPickupLocation(locations) {
      const primaryLocationName = this.normalizeLocationName(this.customPickupPrimaryLocation);

      if (!primaryLocationName) {
        return locations[0];
      }

      return locations.find((location) => {
        const locationName = this.normalizeLocationName(location.name);
        const locationMatch = this.normalizeLocationName(location.match || '');

        return locationName.includes(primaryLocationName) || locationMatch.includes(primaryLocationName);
      }) || locations[0];
    }
    getSortedCustomPickupLocations(locations) {
      const primaryLocation = this.getPrimaryCustomPickupLocation(locations);

      if (!primaryLocation) {
        return locations;
      }

      return [
        primaryLocation,
        ...locations.filter((location) => location.name !== primaryLocation.name)
      ];
    }
    getCustomPickupButtonLabel(locationCount) {
      return locationCount > 1 ? (this.customPickupViewStoresInfoLabel || this.customPickupViewStoreInfoLabel) : this.customPickupViewStoreInfoLabel;
    }
    formatCustomPickupLocationNames(locationNames) {
      if (!locationNames.length) {
        return '';
      }

      if (locationNames.length === 1) {
        return locationNames[0];
      }

      if (locationNames.length === 2) {
        return `${locationNames[0]}${this.customPickupMultipleLocationsJoiner}${locationNames[1]}`;
      }

      return `${locationNames.slice(0, -1).join(', ')}${this.customPickupMultipleLocationsJoiner}${locationNames[locationNames.length - 1]}`;
    }
    formatCustomPickupPreviewLocationNames(locationNames) {
      const normalizedLocationNames = locationNames
        .map((locationName) => (locationName || '').replace(/\s+store$/i, '').trim())
        .filter(Boolean);

      if (!normalizedLocationNames.length) {
        return '';
      }

      if (normalizedLocationNames.length === 1) {
        return `${normalizedLocationNames[0]} store`;
      }

      return `${this.formatCustomPickupLocationNames(normalizedLocationNames)} stores`;
    }
    buildCustomPickupPreview(locations) {
      const sortedLocations = this.getSortedCustomPickupLocations(locations);
      const previewLocationNames = sortedLocations.map((location) => location.name).filter(Boolean);

      if (!previewLocationNames.length) {
        return '';
      }

      const buttonLabel = this.escapeHtml(this.getCustomPickupButtonLabel(previewLocationNames.length));
      const title = this.customPickupTitleTemplate.replace('__LOCATION__', this.escapeHtml(this.formatCustomPickupPreviewLocationNames(previewLocationNames)));

      return `
        <div class="pickup-availability-information">
          ${this.getAvailableIcon()}
          <div class="pickup-availability-information-container">
            <p class="pickup-availability-information__title">${title}</p>
            ${buttonLabel ? `<button class="pickup-availability-information__button text-button">${buttonLabel}</button>` : ''}
          </div>
        </div>
      `;
    }
    buildCustomPickupDrawer(locations) {
      const heading = this.escapeHtml(this.customPickupHeading);
      const sortedLocations = this.getSortedCustomPickupLocations(locations);

      return `
        <div class="side-panel-inner">
          <div class="side-panel-header">
            <div>
              <h4 class="body-font">${heading}</h4>
              <side-panel-close class="side-panel-close">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 1L1 13M13 13L1 1" stroke="var(--color-body)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </side-panel-close>
            </div>
          </div>
          <div class="side-panel-content">
            <ul class="pickup-availability-list" role="list">
              ${sortedLocations.map((location) => `
                <li class="pickup-availability-list__item">
                  <h6 class="pickup-availability-list__location body-font">${this.escapeHtml(location.name)}</h6>
                  <div class="pickup-availability-list__stock">
                    ${this.getAvailableIcon()} ${this.escapeHtml(this.customPickupAvailableLabel)}
                  </div>
                  ${location.addressHtml ? `<address class="pickup-availability-list__address">${location.addressHtml}</address>` : ''}
                  ${location.phoneHtml ? `<p class="pickup-availability-list__phone pickup-availability-small-text">${location.phoneHtml}</p>` : ''}
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
      `;
    }
    onDrawerToggle(evt) {
      const pickupAvailabilityDrawer = document.getElementById('Pickup-Availability');

      if (!pickupAvailabilityDrawer) {
        return;
      }

      pickupAvailabilityDrawer.classList.toggle('active');
      document.getElementsByTagName('body')[0].classList.toggle('open-cc');

      evt.preventDefault();
    }
    attachDrawerToggle() {
      const button = this.querySelector('button');

      if (!button) {
        return;
      }

      button.removeEventListener('click', this.onDrawerToggle);
      button.addEventListener('click', this.onDrawerToggle);
    }
    renderCustomPickup(sectionInnerHTML) {
      // PDP modification: custom pickup preview uses online availability with pickup-enabled stores.
      const locations = this.getConfiguredCustomPickupLocations(this.getCustomPickupLocations(sectionInnerHTML));
      const pickupAvailabilityDrawer = document.getElementById('Pickup-Availability');

      if (!locations.length) {
        this.innerHTML = '';
        this.removeAttribute('available');
        if (pickupAvailabilityDrawer) {
          pickupAvailabilityDrawer.innerHTML = '';
        }
        return;
      }

      this.innerHTML = this.buildCustomPickupPreview(locations);
      this.setAttribute('available', '');

      if (pickupAvailabilityDrawer) {
        pickupAvailabilityDrawer.innerHTML = this.buildCustomPickupDrawer(locations);
      }

      this.attachDrawerToggle();
    }

    fetchAvailability(variantId) {
      let rootUrl = this.dataset.rootUrl;
      if (!rootUrl.endsWith("/")) {
        rootUrl = rootUrl + "/";
      }
      const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

      fetch(variantSectionUrl)
        .then(response => response.text())
        .then(text => {
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.shopify-section');
          if (!sectionInnerHTML) {
            this.renderError();
            return;
          }
          this.renderPreview(sectionInnerHTML);

        })
        .catch(e => {
          const button = this.querySelector('button');
          if (button) button.removeEventListener('click', this.onClickRefreshList);
          this.renderError();
        });
    }

    onClickRefreshList(evt) {
      this.fetchAvailability(this.dataset.variantId);
    }

    renderError() {
      const button = this.querySelector('button');
      if (button) {
        button.removeEventListener('click', this.onClickRefreshList);
        button.addEventListener('click', this.onClickRefreshList);
      }
    }

    renderPreview(sectionInnerHTML) {
      const drawer = document.querySelector('.pickup-availability-drawer');
      if (drawer) drawer.remove();
      if (!sectionInnerHTML) {
        this.renderError();
        return;
      }
      if (!sectionInnerHTML.querySelector('.pickup-availability-information')) {
        this.innerHTML = "";
        this.removeAttribute('available');
        return;
      }

      if (this.customPickupEnabled) {
        this.renderCustomPickup(sectionInnerHTML);
        return;
      }

      this.innerHTML = sectionInnerHTML.querySelector('.pickup-availability-information').innerHTML;
      this.setAttribute('available', '');

      document.getElementById('Pickup-Availability').innerHTML = sectionInnerHTML.querySelector('.side-availability').innerHTML;

      const button = this.querySelector('button');
      if (button) button.addEventListener('click', (evt) => {
        document.getElementById('Pickup-Availability').classList.toggle("active");
        document.getElementsByTagName("body")[0].classList.toggle('open-cc');

        evt.preventDefault();
      });
    }
  });
}
